import { useEffect, useRef, useState, type ReactNode } from 'react';
import { enrollTotp } from '../api/client';
import type { Stage, TotpEnrollment } from '../api/schemas';
import { ErrorBox, Loading } from '../ui/elements';
import { TotpForm } from './TotpForm';
import { toApiError } from './LoginPage';

/**
 * Die Einrichtung des zweiten Faktors.
 *
 * **Die Einrichtung wird genau einmal angefordert.** `POST /auth/totp/enroll`
 * erzeugt jedes Mal ein **neues** Geheimnis und legt es verschlüsselt ab. Ein
 * zweiter Aufruf machte den eben angezeigten QR-Code stillschweigend ungültig:
 * Der Anwender hätte das erste Geheimnis in seiner App und der Server das
 * zweite in der Datenbank — die Bestätigung schlüge fehl, ohne dass jemand den
 * Grund sähe. Deshalb der Wächter über `useRef` und deshalb kein
 * `StrictMode` in `main.tsx`.
 *
 * **Das Geheimnis steht im Klartext daneben.** Nicht jeder Rechner hat eine
 * Kamera, und ein QR-Code ohne abtippbare Entsprechung ist für den, der keine
 * hat, eine verschlossene Tür. Es erscheint genau einmal — der Server
 * speichert es nur verschlüsselt und gibt es nie wieder heraus.
 */
export function TotpEnrollForm(props: {
  readonly onStage: (stage: Stage) => void;
  readonly onFailure: (error: unknown) => void;
}): ReactNode {
  const [enrollment, setEnrollment] = useState<TotpEnrollment | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;

    void (async () => {
      try {
        setEnrollment(await enrollTotp());
      } catch (error) {
        setFailure(toApiError(error).message);
      }
    })();
  }, []);

  if (failure !== undefined) return <ErrorBox message={failure} />;
  if (enrollment === undefined) return <Loading what="Die Einrichtung" />;

  return (
    <div className="space-y-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
        <li>QR-Code mit der Authenticator-App aufnehmen oder den Schlüssel eintippen.</li>
        <li>Das angezeigte sechsstellige Kennwort hier eingeben.</li>
      </ol>

      <div className="flex flex-col items-center gap-3 rounded-[10px] border border-border bg-secondary p-4">
        {/* Der QR-Code kommt als Daten-URL aus der Antwort — kein Nachladen von aussen. */}
        <img
          src={enrollment.qrDataUrl}
          alt="QR-Code zur Einrichtung des Einmalkennworts"
          // Der QR-Code bleibt WEISS, auch im Dunkelmodus. Er ist kein Text,
          // sondern ein optisches Muster, das eine Kamera lesen muss: Sie
          // erwartet dunkle Module auf hellem Grund. Ein grauer Untergrund
          // senkt den Kontrast des Musters, und ein invertierter macht es fuer
          // die meisten Lesegeraete unlesbar.
          className="h-44 w-44 bg-white"
        />
        <div className="text-center">
          <p className="text-xs text-muted-foreground">Schlüssel zum Abtippen</p>
          <code data-testid="totp-secret" className="zahl break-all text-sm text-foreground">
            {enrollment.secret}
          </code>
        </div>
      </div>

      <TotpForm
        label="Einmalkennwort aus der App"
        onStage={props.onStage}
        onFailure={props.onFailure}
      />
    </div>
  );
}
